import { Monster } from './Monster';
import { Earth } from '../earth/Earth';
import Phaser from 'phaser';
import { AudioManager, SoundEffect } from '../../systems/AudioManager';

export class SplittingAlien extends Monster {
  private hasSplit: boolean = false;
  private isSmall: boolean = false; // New property to track if it's a mini alien

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.health = 40;
    this.maxHealth = 40;
    this.speed = 32;
    this.reward = 12;
    this.specialAbility = 'Splits into 2 on death';

    // Visual representation
    const circle = scene.add.circle(0, 0, 8, 0x8e44ad);
    circle.setName('monsterCircle');
    this.add(circle);
  }

  override update(time: number, delta: number): void {
    super.update(time, delta);
    if (!this.targetEarth) {
      this.findEarth();
    }
    if (this.targetEarth) {
      this.move();
    }
  }

  override move(): void {
    if (!this.targetEarth) return;
    const direction = new Phaser.Math.Vector2(
      this.targetEarth.x - this.x,
      this.targetEarth.y - this.y
    ).normalize();
    this.x += direction.x * this.speed * 0.016;
    this.y += direction.y * this.speed * 0.016;
    const distance = Phaser.Math.Distance.Between(this.x, this.y, this.targetEarth.x, this.targetEarth.y);
    if (distance < 60) {
      this.attackEarth();
    }
  }

  override attackEarth(): void {
    const _this = this;
    if (!this.targetEarth) return;
    
    // Create splitting attack effect
    this.createSplittingAttackEffect();
    
    // Splitting aliens deal damage based on size
    const baseDamage = this.isSmall ? 6 : 11;
    if (this.isActive) this.targetEarth.takeDamage(baseDamage);
    
    // Play attack sound
    const audioManager = AudioManager.getInstance(this.scene);
    audioManager.playSFX(SoundEffect.EARTH_DAMAGE);
    
    // Mark as inactive before destroying
    this.isActive = false;
    
    // Splitting death explosion
    this.createSplittingDeathExplosion();
    
    this.scene.time.delayedCall(80, () => {
      _this.destroy();
    });
  }

  private createSplittingAttackEffect(): void {
    if (!this.scene) return;
    // Multiple small impacts for splitting effect
    const impactCount = this.isSmall ? 3 : 5;
    
    for (let i = 0; i < impactCount; i++) {
      const offsetX = (Math.random() - 0.5) * 20;
      const offsetY = (Math.random() - 0.5) * 20;
      
      const impact = this.scene.add.circle(
        this.x + offsetX, 
        this.y + offsetY, 
        this.isSmall ? 6 : 10, 
        0xff44ff, 
        0.7
      );
      
      this.scene.tweens.add({
        targets: impact,
        scaleX: 1.5,
        scaleY: 1.5,
        alpha: 0,
        duration: 250,
        delay: i * 40,
        ease: 'Power2',
        onComplete: () => impact.destroy()
      });
    }
    
    // Splitting energy burst
    const burst = this.scene.add.circle(this.x, this.y, this.isSmall ? 15 : 22, 0xaa44aa, 0.4);
    this.scene.tweens.add({
      targets: burst,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 300,
      ease: 'Power2',
      onComplete: () => burst.destroy()
    });
  }

  private createSplittingDeathExplosion(): void {
    // Purple fragmentation explosion
    const mainExplosion = this.scene.add.circle(
      this.x, 
      this.y, 
      this.isSmall ? 6 : 9, 
      0xff44ff, 
      0.8
    );
    
    this.scene.tweens.add({
      targets: mainExplosion,
      scaleX: this.isSmall ? 2 : 2.5,
      scaleY: this.isSmall ? 2 : 2.5,
      alpha: 0,
      duration: 250,
      ease: 'Power2',
      onComplete: () => mainExplosion.destroy()
    });
    
    // Fragment particles
    const fragmentCount = this.isSmall ? 4 : 6;
    for (let i = 0; i < fragmentCount; i++) {
      const angle = (Math.PI * 2 * i) / fragmentCount;
      const fragment = this.scene.add.circle(this.x, this.y, 2, 0xaa44aa);
      
      this.scene.tweens.add({
        targets: fragment,
        x: this.x + Math.cos(angle) * 20,
        y: this.y + Math.sin(angle) * 20,
        alpha: 0,
        duration: 350,
        ease: 'Power2',
        onComplete: () => fragment.destroy()
      });
    }
  }

  override activateAbility(): void {
    // Splitting is passive
  }

  override destroy(fromScene?: boolean): void {
    if (!this.hasSplit && this.scene) {
      this.hasSplit = true;
      this.spawnMiniAliens();
    }
    super.destroy();
  }

  private spawnMiniAliens(): void {
    for (let i = 0; i < 2; i++) {
      const angle = Math.random() * Math.PI * 2;
      const offset = 16;
      const x = this.x + Math.cos(angle) * offset;
      const y = this.y + Math.sin(angle) * offset;
      const mini = new MiniAlien(this.scene, x, y);
      this.scene.add.existing(mini);
    }
  }
}

// MiniAlien: nhỏ hơn, yếu hơn, không split tiếp
class MiniAlien extends Monster {

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.health = 12;
    this.maxHealth = 12;
    this.speed = 36;
    this.reward = 4;
    this.specialAbility = 'None';
    const circle = scene.add.graphics();
    circle.fillStyle(0xbb8fce);
    circle.fillCircle(0, 0, 5);
    circle.setName('monsterCircle');
    this.add(circle);
  }

  override update(time: number, delta: number): void {
    if (!this.targetEarth) {
      this.findEarth();
    }
    if (this.targetEarth) {
      this.move();
    }
  }

  override move(): void {
    if (!this.targetEarth) return;
    const direction = new Phaser.Math.Vector2(
      this.targetEarth.x - this.x,
      this.targetEarth.y - this.y
    ).normalize();
    this.x += direction.x * this.speed * 0.016;
    this.y += direction.y * this.speed * 0.016;
    const distance = Phaser.Math.Distance.Between(this.x, this.y, this.targetEarth.x, this.targetEarth.y);
    if (distance < 60) {
      this.attackEarth();
    }
  }

  override attackEarth(): void {
    const _this = this;
    if (!this.targetEarth) return;
    if (this.isActive) this.targetEarth.takeDamage(4);
    _this.destroy();
  }

  override activateAbility(): void {
    // No special ability
  }
} 